import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { TanStackQueryIds } from '../../query/query-ids'
import { APIError } from '~~/src/api/_error/APIError'
import type { ElemetalInfosData } from '~~/src/api/elementals'
import { removeElementalVote, voteElemental } from '~~/src/api/elementals'
import type { Elemental, ElementalTypeEnum } from '~~/src/types/elemental'

function update(data: Elemental | undefined) {
  if (!data)
    return data
  data.nVotes = data.nVotes ?? 0
  data.isVoted = !data.isVoted
  return {
    ...data,
    nVotes: data.isVoted ? data.nVotes + 1 : data.nVotes - 1,
  }
}

function updateInfo(data: ElemetalInfosData | undefined) {
  if (!data)
    return data
  const votes = data.counts?.votes ?? 0
  return {
    ...data,
    isVoted: !data.isVoted,
    counts: {
      ...data.counts,
      votes: !data.isVoted ? votes + 1 : votes - 1,
    },
  } as ElemetalInfosData
}

function revert(elemental: Elemental) {
  return (data: Elemental | undefined) => {
    if (!data)
      return data
    data.nVotes = elemental.nVotes
    data.isVoted = elemental.isVoted
    return {
      ...data,
      nVotes: elemental.nVotes,
      isVoted: elemental.isVoted,
    }
  }
}
function revertInfo(elemental: Elemental) {
  return (data: ElemetalInfosData | undefined) => {
    if (!data)
      return data
    const votes = data.counts?.votes ?? 0
    return {
      ...data,
      isVoted: !!elemental.isVoted,
      counts: {
        ...data.counts,
        votes: elemental.isVoted ? votes + 1 : votes - 1,
      },
    } as ElemetalInfosData
  }
}

interface useCreateCommentArgs {
  elemental: Elemental
  elementalType: ElementalTypeEnum
}

export const useUpVoteMutation = () => {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation<Awaited<ReturnType<typeof voteElemental>>, Error, useCreateCommentArgs>({
    mutationKey: ['upvote'],
    mutationFn: async ({ elemental }) => {
      if (elemental.isVoted)
        return await removeElementalVote(elemental.id)
      return await voteElemental(elemental.id)
    },
    onMutate: ({ elemental, elementalType }) => {
      queryClient.setQueriesData({
        queryKey: [TanStackQueryIds.ELEMENTAL, elementalType, elemental.id],
      }, update)
      queryClient.setQueriesData({
        queryKey: [TanStackQueryIds.ELEMENTAL, elementalType, elemental.url],
      }, update)
      queryClient.setQueriesData({
        queryKey: [TanStackQueryIds.ELEMENTAL_INFOS, elemental.id],
      }, updateInfo)
    },
    onError: (error, { elemental, elementalType }) => {
      queryClient.setQueriesData({
        queryKey: [TanStackQueryIds.ELEMENTAL, elementalType, elemental.id],
      }, revert(elemental))
      queryClient.setQueriesData({
        queryKey: [TanStackQueryIds.ELEMENTAL, elementalType, elemental.url],
      }, revert(elemental))
      queryClient.setQueriesData({
        queryKey: [TanStackQueryIds.ELEMENTAL_INFOS, elemental.id],
      }, revertInfo(elemental))

      if (error instanceof APIError) {
        toast.error(error.message)
        return
      }
      toast.error(error.message)
    },
    onSuccess(_, { elemental }) {
      queryClient.invalidateQueries({
        queryKey: [TanStackQueryIds.LIST_ITEMS_INFINITY],
      })
      const type = (elemental.isList) ? 'list' : 'prompt'
      if (elemental.isVoted)
        sendGTMEvent({ event: `${type}-remove-upvote` })
      else
        sendGTMEvent({ event: `${type}-upvote` })
    },
  })
}
